import { useState, useMemo, useCallback } from "react";
import {
  Mail, MailCheck, MailX, MailWarning, Paperclip, ChevronDown,
  RefreshCw, Loader2, AlertCircle, Info, Send,
} from "lucide-react";
import { useGetBookingEmails } from "../../../../api/communication/useEmailsApi";
import { useToast } from "../../../../context/ToastContext";
import { formatRelative } from "../documents/shared";
import base from "../BookingDetail.module.css";
import s from "./emails.module.css";

/* Status → the icon and class fragments the row needs. Kept as one map so a
   status can never pick up a colour from one place and a label from another. */
const TONE = {
  sent: { Icon: MailCheck, dot: "dotSent", chip: "statusSent" },
  failed: { Icon: MailX, dot: "dotFailed", chip: "statusFailed" },
  skipped: { Icon: MailWarning, dot: "dotSkipped", chip: "statusSkipped" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "SENT", label: "Delivered" },
  { key: "FAILED", label: "Failed" },
  { key: "SKIPPED", label: "Not sent" },
];

const formatBytes = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return null;
  const kb = Number(bytes) / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const formatTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

/* Group header: "Today" / "Yesterday" for the two days an operator is usually
   looking at, an absolute date beyond that. */
const dayLabel = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const EmailRow = ({ email, isLast, index }) => {
  const [open, setOpen] = useState(false);
  const tone = TONE[email.tone] || TONE.skipped;
  const { Icon } = tone;

  return (
    <div
      className={`${s.row} ${isLast ? s.rowLast : ""}`}
      style={{ animation: `mailSlideIn 0.3s ease-out ${Math.min(index, 8) * 0.04}s both` }}
    >
      <div className={s.rail}>
        <div className={`${s.dot} ${s[tone.dot]}`} />
      </div>

      <div className={`${s.card} ${email.status === "FAILED" ? s.cardFailed : ""}`}>
        <div className={s.cardTop}>
          <div className={s.cardMain}>
            <div className={s.subject} title={email.subject}>{email.subject}</div>
            <div className={s.meta}>
              <span className={`${s.chip} ${s[tone.chip]}`}>
                <Icon size={11} />
                {email.statusLabel}
              </span>
              <span className={`${s.chip} ${s.chipCategory}`}>{email.categoryLabel}</span>
              <span className={s.recipient}>{email.toEmail}</span>
              {email.attachments.map((f, i) => (
                <span key={i} className={s.attachChip} title={f.name}>
                  <Paperclip size={10} />
                  {f.name}
                  {formatBytes(f.bytes) ? ` · ${formatBytes(f.bytes)}` : ""}
                </span>
              ))}
            </div>
          </div>

          <div className={s.cardRight}>
            <span className={s.time} title={email.sentAt ? formatRelative(email.sentAt) : ""}>
              {formatTime(email.sentAt)}
            </span>
            <button
              type="button"
              className={`${s.expandBtn} ${open ? s.expandOpen : ""}`}
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Hide details" : "Show details"}
              title={open ? "Hide details" : "Show details"}
            >
              <ChevronDown size={13} />
            </button>
          </div>
        </div>

        {email.status === "FAILED" && email.error && (
          <div className={s.errorBanner}>
            <AlertCircle size={14} />
            <span><strong>Rejected by the mail provider:</strong> {email.error}</span>
          </div>
        )}

        {email.status === "SKIPPED" && (
          <div className={s.skippedBanner}>
            <AlertCircle size={14} />
            <span>
              No send was attempted — the mail provider isn’t configured on this server yet.
              This email will not reach the customer until an API key is added.
            </span>
          </div>
        )}

        {open && (
          <div className={s.details}>
            <div className={s.detailItem}>
              <div className={s.detailKey}>Sent at</div>
              <div className={s.detailVal}>
                {email.sentAt ? new Date(email.sentAt).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
              </div>
            </div>
            <div className={s.detailItem}>
              <div className={s.detailKey}>To</div>
              <div className={s.detailVal}>
                {email.toName ? `${email.toName} · ` : ""}{email.toEmail}
              </div>
            </div>
            <div className={s.detailItem}>
              <div className={s.detailKey}>From</div>
              <div className={s.detailVal}>{email.fromEmail}</div>
            </div>
            <div className={s.detailItem}>
              <div className={s.detailKey}>Provider message ID</div>
              <div className={`${s.detailVal} ${s.mono}`}>{email.messageId || "—"}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, valueClass, alert }) => (
  <div className={`${s.summaryCard} ${alert ? s.summaryCardAlert : ""}`}>
    <div className={s.summaryLabel}>{label}</div>
    <div className={`${s.summaryValue} ${valueClass ? s[valueClass] : ""}`}>{value}</div>
  </div>
);

const Skeleton = ({ count = 4 }) => (
  <div>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={s.skelRow}>
        <div className={s.skelDot}>
          <div className={`${s.skelBlock} ${s.shimmer}`} style={{ width: 10, height: 10, borderRadius: "50%", marginTop: 16 }} />
        </div>
        <div className={s.skelCard}>
          <div className={`${s.skelBlock} ${s.shimmer}`} style={{ width: "46%", height: 12 }} />
          <div className={`${s.skelBlock} ${s.shimmer}`} style={{ width: "72%", height: 10 }} />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Email delivery log for one booking (spec §9.1.3).
 *
 * Read-only by design — it reports what the mailer actually did. Every send,
 * including the ones that never left the server, appears here so "the customer
 * says they never got it" has an answer that doesn't need the provider console.
 */
const EmailsTab = ({ bookingRef }) => {
  const { data: emails, meta, loading, error, refetch, refetchSilent } = useGetBookingEmails(bookingRef);
  const { showToast } = useToast();

  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    const ok = await refetchSilent();
    setRefreshing(false);
    showToast(
      ok ? "Email log refreshed" : "Could not refresh the email log",
      ok ? "success" : "error"
    );
  }, [refetchSilent, showToast]);

  const counts = useMemo(
    () => ({
      all: meta.total,
      SENT: meta.sent,
      FAILED: meta.failed,
      SKIPPED: meta.skipped,
    }),
    [meta]
  );

  const visible = useMemo(
    () => (filter === "all" ? emails : emails.filter((e) => e.status === filter)),
    [emails, filter]
  );

  // Group into days while preserving the newest-first order the API returned.
  const groups = useMemo(() => {
    const out = [];
    for (const email of visible) {
      const label = dayLabel(email.sentAt);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(email);
      else out.push({ label, items: [email] });
    }
    return out;
  }, [visible]);

  if (loading) return <Skeleton />;

  if (error) {
    return (
      <div className={s.empty}>
        <div className={s.emptyIcon}><AlertCircle size={26} /></div>
        <div className={s.emptyTitle}>Couldn’t load the email log</div>
        <div className={s.emptyHint}>{error}</div>
        <div style={{ marginTop: 12 }}>
          <button className={base.btnSecondary} onClick={() => refetch()}>
            <RefreshCw size={12} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!emails.length) {
    return (
      <div className={s.empty}>
        <div className={s.emptyIcon}><Send size={26} /></div>
        <div className={s.emptyTitle}>No emails sent for this booking yet</div>
        <div className={s.emptyHint}>
          Every email this booking generates — receipts, invoices, vouchers and confirmations —
          is recorded here with its delivery result, so you can confirm what actually reached the customer.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={s.summaryStrip}>
        <SummaryCard label="Total" value={meta.total} />
        <SummaryCard label="Delivered" value={meta.sent} valueClass="valueSent" />
        <SummaryCard label="Failed" value={meta.failed} valueClass="valueFailed" alert={meta.failed > 0} />
        <SummaryCard label="Not sent" value={meta.skipped} valueClass="valueSkipped" alert={meta.skipped > 0} />
      </div>

      {meta.skipped > 0 && (
        <div className={s.infoBanner}>
          <Info size={14} />
          <span>
            <strong>{meta.skipped} email{meta.skipped !== 1 ? "s were" : " was"} never sent.</strong>{" "}
            The mail provider has no API key configured on this server, so these were recorded
            but not delivered. They need to be re-sent once mail is switched on.
          </span>
        </div>
      )}

      <div className={s.toolbar}>
        <div className={s.segmentBar}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${s.segment} ${filter === f.key ? s.segmentActive : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className={s.segmentCount}>{counts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>

        <button className={base.btnSecondary} onClick={handleRefresh} disabled={refreshing}>
          {refreshing
            ? <Loader2 size={12} style={{ animation: "mailSpin 1s linear infinite" }} />
            : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {!visible.length ? (
        <div className={s.empty}>
          <div className={s.emptyIcon}><Mail size={26} /></div>
          <div className={s.emptyTitle}>Nothing in this view</div>
          <div className={s.emptyHint}>
            No emails match the “{FILTERS.find((f) => f.key === filter)?.label}” filter.
          </div>
        </div>
      ) : (
        groups.map((group, gi) => (
          <div key={`${group.label}-${gi}`} className={s.dayGroup}>
            <div className={s.dayLabel}>{group.label}</div>
            {group.items.map((email, i) => (
              <EmailRow
                key={email.id}
                email={email}
                index={i}
                isLast={i === group.items.length - 1}
              />
            ))}
          </div>
        ))
      )}

      {meta.truncated && (
        <div className={s.truncNote}>
          Showing the most recent {emails.length} emails for this booking.
        </div>
      )}
    </div>
  );
};

export default EmailsTab;
